import { inject, provider } from "react-ioc";
import { observer } from "mobx-react";
import React, { Component } from "react";
import { Container, ContainerDetails } from "app/modules/common/layout/components/containers";
import { PageTitleDetails } from "app/modules/common/layout/components/page-title";
import { AutoBidSettingsStore } from "app/modules/company/components/settings/auto-bid-settings/AutoBidSettingsStore";
import { Loading } from "app/modules/common/layout/components/loading";
import {
    Box,
    Checkbox,
    FormControlLabel,
    Stack,
    Typography,
    Button,
    TextField
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Section } from "app/modules/common/layout/components/details";
import { SectionTextField } from "app/modules/common/details/text-field";
import { DateTimePicker } from "@mui/x-date-pickers-pro";
import Moment from "moment";

@provider(AutoBidSettingsStore)
@observer
export default class Index extends Component<any, any> {
    @inject autoBidSettingsStore: AutoBidSettingsStore;

    render() {
        if (this.autoBidSettingsStore.isInitLoading) {
            return <Loading />;
        }

        return (
            <Container>
                <PageTitleDetails Title={this.autoBidSettingsStore.title} Size={12} />
                <ContainerDetails>
                    <Section>
                        <Stack spacing={3}>
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Auto Bid Rate Settings
                                </Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    Configure the default bid rate that will be used when creating new bids rate.
                                </Typography>
                            </Box>
                            <Box>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={this.autoBidSettingsStore.bidRateSettingsFormState.$.enableAutoBid.value}
                                            onChange={(e) => {
                                                this.autoBidSettingsStore.setEnableAutoBid(e.target.checked);
                                            }}
                                        />
                                    }
                                    label="Enable Auto Bid"
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Enable automatic bidding
                                </Typography>
                            </Box>
                            {this.autoBidSettingsStore.bidRateSettingsFormState.$.enableAutoBid.value && (
                                <Box>
                                    <Stack spacing={2} direction="row" alignItems="center">
                                        <DateTimePicker
                                            label="Disable Auto Bid At"
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="standard"
                                                    style={{ width: "300px" }}
                                                    error={this.autoBidSettingsStore.bidRateSettingsFormState.$.disableAt.hasError}
                                                    helperText={this.autoBidSettingsStore.bidRateSettingsFormState.$.disableAt.error}
                                                />
                                            )}
                                            value={this.autoBidSettingsStore.bidRateSettingsFormState.$.disableAt.value}
                                            onChange={this.autoBidSettingsStore.bidRateSettingsFormState.$.disableAt.onChange}
                                            minutesStep={15}
                                        />
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => {
                                                this.autoBidSettingsStore.bidRateSettingsFormState.$.disableAt.onChange(null);
                                            }}
                                        >
                                            Clear
                                        </Button>
                                    </Stack>
                                    <Stack spacing={1} direction="row" sx={{ mt: 1 }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => {
                                                this.autoBidSettingsStore.bidRateSettingsFormState.$.disableAt.onChange(Moment().add(8, 'hours'));
                                            }}
                                        >
                                            +8 hrs
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => {
                                                this.autoBidSettingsStore.bidRateSettingsFormState.$.disableAt.onChange(Moment().add(10, 'hours'));
                                            }}
                                        >
                                            +10 hrs
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => {
                                                this.autoBidSettingsStore.bidRateSettingsFormState.$.disableAt.onChange(Moment().add(12, 'hours'));
                                            }}
                                        >
                                            +12 hrs
                                        </Button>
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                        Set a date and time when auto bidding should be automatically disabled
                                    </Typography>
                                </Box>
                            )}
                            <Box>
                                <SectionTextField
                                    label="Default Bid Rate (%)"
                                    type="number"
                                    field={this.autoBidSettingsStore.bidRateSettingsFormState.$.rate}
                                    width="300px"
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Enter the default rate in dollars that will be used for new bids
                                </Typography>
                            </Box>
                            <Box>
                                <SectionTextField
                                    label="Allowed Rate Range (±%)"
                                    type="number"
                                    field={this.autoBidSettingsStore.bidRateSettingsFormState.$.allowedRateRange}
                                    width="300px"
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    Enter the allowed range in percentage (±) for rate variations
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
                                <LoadingButton
                                    loading={this.autoBidSettingsStore.isLoading}
                                    variant="contained"
                                    onClick={this.autoBidSettingsStore.saveBidRateSettings}
                                    size="large"
                                >
                                    Save Auto Bid Rate Settings
                                </LoadingButton>
                            </Box>
                        </Stack>
                    </Section>
                </ContainerDetails>
            </Container>
        );
    }

    async componentDidMount() {
        await this.autoBidSettingsStore.init();
    }
}
